import React, { useState, useEffect, useRef, useCallback } from 'react';
import api, { errorMessage, asArray } from '../api';
import { useAuth } from '../context/AuthContext';
import { useSocket } from '../context/SocketContext';
import Avatar from './Avatar';
import MeetChip from './MeetChip';
import { BackIcon, ShareIcon } from './icons';
import { formatListTime, formatDaySeparator, formatTime, isDifferentDay } from '../lib/datetime';

const PREVIEW_EMPTY = 'Inga meddelanden än';

export default function Chat({ onViewProfile, onMessagesRead, onOpenMeet }) {
  const { user } = useAuth();
  const { socket, connected } = useSocket();

  const [tab, setTab] = useState('dm');
  const [friends, setFriends] = useState([]);
  const [pending, setPending] = useState([]);
  const [groups, setGroups] = useState([]);
  const [conversation, setConversation] = useState(null); // { type, id, name, picture }
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [addUsername, setAddUsername] = useState('');
  const [status, setStatus] = useState(null);
  const [loadingMessages, setLoadingMessages] = useState(false);

  const [showAddFriend, setShowAddFriend] = useState(false);
  const [showCreateGroup, setShowCreateGroup] = useState(false);
  const [groupName, setGroupName] = useState('');
  const [selectedMembers, setSelectedMembers] = useState([]);

  const messagesEndRef = useRef(null);
  const reloadTimer = useRef(null);

  const notify = (type, text, timeout = 3000) => {
    setStatus({ type, text });
    if (timeout) setTimeout(() => setStatus(null), timeout);
  };

  const loadLists = useCallback(async () => {
    try {
      const [friendsRes, pendingRes, groupsRes] = await Promise.all([
        api.get('/friends'),
        api.get('/friends/pending'),
        api.get('/group-chats'),
      ]);
      setFriends(asArray(friendsRes.data));
      setPending(asArray(pendingRes.data));
      setGroups(asArray(groupsRes.data));
    } catch (err) {
      notify('error', errorMessage(err, 'Kunde inte hämta vänner och grupper.'));
    }
  }, []);

  useEffect(() => { loadLists(); }, [loadLists]);

  useEffect(() => () => clearTimeout(reloadTimer.current), []);

  /**
   * Inkommande meddelanden hör bara hemma i den tråd som är öppen. Är ingen
   * tråd öppen uppdateras i stället listan, så att förhandsvisningarna är
   * färska på samma sätt som i en meddelandeapp.
   */
  useEffect(() => {
    if (!socket) return undefined;

    const belongsHere = (message) => {
      if (!conversation) return false;
      if (conversation.type === 'group') return message.group_chat_id === conversation.id;
      return (
        message.group_chat_id === undefined &&
        (message.sender_id === conversation.id || message.receiver_id === conversation.id)
      );
    };

    const onMessage = (message) => {
      if (belongsHere(message)) {
        setMessages(prev => (prev.some(m => m.id === message.id) ? prev : [...prev, message]));
        return;
      }
      clearTimeout(reloadTimer.current);
      reloadTimer.current = setTimeout(loadLists, 400);
    };

    const onError = (payload) => notify('error', payload.error || 'Meddelandet kunde inte skickas.');

    socket.on('chat:message', onMessage);
    socket.on('group:message', onMessage);
    socket.on('chat:error', onError);

    return () => {
      socket.off('chat:message', onMessage);
      socket.off('group:message', onMessage);
      socket.off('chat:error', onError);
    };
  }, [socket, conversation, loadLists]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const openConversation = async (next) => {
    setConversation(next);
    setMessages([]);
    setLoadingMessages(true);
    try {
      const url = next.type === 'group'
        ? `/group-chats/${next.id}/messages`
        : `/chat/${next.id}`;
      const res = await api.get(url);
      setMessages(asArray(res.data));
      if (next.type === 'dm' && onMessagesRead) onMessagesRead();
    } catch (err) {
      notify('error', errorMessage(err, 'Meddelandena kunde inte hämtas.'));
    } finally {
      setLoadingMessages(false);
    }
  };

  const closeConversation = () => {
    setConversation(null);
    setMessages([]);
    // Listan ska visa det som just skrevs, och olästmarkeringen ska vara borta.
    loadLists();
    if (onMessagesRead) onMessagesRead();
  };

  const send = () => {
    const content = input.trim();
    if (!content || !conversation) return;

    if (!socket || !connected) {
      notify('error', 'Ingen kontakt med servern just nu.');
      return;
    }

    const event = conversation.type === 'group' ? 'group:message' : 'chat:message';
    const payload = conversation.type === 'group'
      ? { groupId: conversation.id, content }
      : { to: conversation.id, content };

    socket.emit(event, payload, (response) => {
      if (response && !response.ok) notify('error', response.error);
    });
    setInput('');
  };

  const sendFriendRequest = async () => {
    const username = addUsername.trim();
    if (!username) return;
    try {
      const res = await api.post('/friends/request', { username });
      notify('success', res.data.message);
      setAddUsername('');
      setShowAddFriend(false);
      loadLists();
    } catch (err) {
      notify('error', errorMessage(err));
    }
  };

  const respondToRequest = async (friendshipId, accept) => {
    try {
      if (accept) await api.post(`/friends/accept/${friendshipId}`);
      else await api.delete(`/friends/${friendshipId}`);
      loadLists();
    } catch (err) {
      notify('error', errorMessage(err));
    }
  };

  const createGroup = async () => {
    if (!groupName.trim() || selectedMembers.length === 0) {
      notify('error', 'Ge gruppen ett namn och välj minst en vän.');
      return;
    }
    try {
      await api.post('/group-chats', { name: groupName.trim(), memberIds: selectedMembers });
      setShowCreateGroup(false);
      setGroupName('');
      setSelectedMembers([]);
      loadLists();
    } catch (err) {
      notify('error', errorMessage(err, 'Gruppen kunde inte skapas.'));
    }
  };

  const leaveGroup = async (groupId) => {
    try {
      await api.delete(`/group-chats/${groupId}/members/me`);
      closeConversation();
    } catch (err) {
      notify('error', errorMessage(err));
    }
  };

  const toggleMember = (id) => {
    setSelectedMembers(prev => (
      prev.includes(id) ? prev.filter(m => m !== id) : [...prev, id]
    ));
  };

  const statusLine = status && <p className={`notice notice--${status.type}`}>{status.text}</p>;

  // ---------- Öppen konversation ----------
  if (conversation) {
    const isGroup = conversation.type === 'group';

    return (
      <div className="chat">
        <div className="chat__header">
          <button className="btn btn--back" onClick={closeConversation} aria-label="Tillbaka">
            <BackIcon size={20} />
          </button>
          <Avatar src={conversation.picture} name={conversation.name} size="sm" />
          <h3 className="chat__title">{conversation.name}</h3>
          {!isGroup && onViewProfile && (
            <button className="btn btn--sm btn--outline" onClick={() => onViewProfile(conversation.id)}>
              Profil
            </button>
          )}
          {isGroup && (
            <button className="btn btn--sm btn--neutral" onClick={() => leaveGroup(conversation.id)}>
              Lämna
            </button>
          )}
        </div>

        {statusLine}

        <div className="chat__messages">
          {loadingMessages && <p className="empty">Hämtar meddelanden...</p>}
          {!loadingMessages && messages.length === 0 && (
            <p className="empty">Inga meddelanden än. Säg hej!</p>
          )}

          {messages.map((message, index) => {
            const own = message.sender_id === user.id;
            const previous = messages[index - 1];
            // Datumrubrik när dygnet byts, så att gammalt syns som gammalt.
            const newDay = !previous || isDifferentDay(previous.created_at, message.created_at);

            return (
              <React.Fragment key={message.id ?? index}>
                {newDay && (
                  <div className="day-divider">
                    <span>{formatDaySeparator(message.created_at)}</span>
                  </div>
                )}
                <div className={`message ${own ? 'message--own' : ''}`}>
                  {isGroup && !own && (
                    <small className="message__sender">{message.sender_name}</small>
                  )}
                  {message.meet && (
                    <MeetChip meet={message.meet} onOpen={() => onOpenMeet && onOpenMeet(message.meet)} />
                  )}
                  {message.content && <div className="message__body">{message.content}</div>}
                  <span className="message__time">{formatTime(message.created_at)}</span>
                </div>
              </React.Fragment>
            );
          })}
          <div ref={messagesEndRef} />
        </div>

        <div className="chat__input">
          <input
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') send(); }}
            placeholder={connected ? 'Skriv meddelande...' : 'Väntar på anslutning...'}
            maxLength={2000}
          />
          <button className="btn btn--primary btn--icon" onClick={send} disabled={!connected} aria-label="Skicka">
            <ShareIcon size={19} />
          </button>
        </div>
      </div>
    );
  }

  // ---------- Listvy ----------
  return (
    <div>
      <div className="tabs">
        <button className={tab === 'dm' ? 'is-active' : ''} onClick={() => setTab('dm')}>Meddelanden</button>
        <button className={tab === 'group' ? 'is-active' : ''} onClick={() => setTab('group')}>Grupper</button>
      </div>

      {statusLine}

      {tab === 'dm' && (
        <div>
          {pending.length > 0 && (
            <div className="request-list">
              <h4 className="section-label">Vänförfrågningar</h4>
              {pending.map(request => (
                <div key={request.friendship_id} className="convo convo--request">
                  <Avatar src={request.profile_picture} name={request.username} />
                  <span className="convo__body">
                    <span className="convo__name">{request.username}</span>
                    <span className="convo__preview">vill bli vän med dig</span>
                  </span>
                  <span className="row">
                    <button className="btn btn--sm btn--primary" onClick={() => respondToRequest(request.friendship_id, true)}>
                      Ja
                    </button>
                    <button className="btn btn--sm btn--neutral" onClick={() => respondToRequest(request.friendship_id, false)}>
                      Nej
                    </button>
                  </span>
                </div>
              ))}
            </div>
          )}

          <div className="row row--spread section-head">
            <h4 className="section-label">Vänner</h4>
            <button className="btn btn--sm btn--outline" onClick={() => setShowAddFriend(!showAddFriend)}>
              {showAddFriend ? 'Avbryt' : '+ Lägg till'}
            </button>
          </div>

          {showAddFriend && (
            <div className="inline-form">
              <input
                className="grow"
                placeholder="Användarnamn"
                value={addUsername}
                onChange={e => setAddUsername(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') sendFriendRequest(); }}
                autoFocus
              />
              <button className="btn btn--primary" onClick={sendFriendRequest}>Skicka</button>
            </div>
          )}

          {friends.length === 0 ? (
            <p className="empty">Inga vänner ännu. Lägg till någon med användarnamnet!</p>
          ) : friends.map(friend => {
            const unread = friend.unread_count || 0;
            const ownLast = friend.last_message_sender_id === user.id;

            return (
              <button
                key={friend.friendship_id}
                className={`convo ${unread > 0 ? 'convo--unread' : ''}`}
                onClick={() => openConversation({
                  type: 'dm', id: friend.id, name: friend.username, picture: friend.profile_picture,
                })}
              >
                <Avatar src={friend.profile_picture} name={friend.username} sharing={friend.share_location} />

                <span className="convo__body">
                  <span className="convo__top">
                    <span className="convo__name">{friend.username}</span>
                    <span className="convo__time">{formatListTime(friend.last_message_at)}</span>
                  </span>
                  <span className="convo__bottom">
                    <span className="convo__preview">
                      {friend.last_message
                        ? `${ownLast ? 'Du: ' : ''}${friend.last_message}`
                        : PREVIEW_EMPTY}
                    </span>
                    {unread > 0 && <span className="convo__unread">{unread}</span>}
                  </span>
                </span>
              </button>
            );
          })}
        </div>
      )}

      {tab === 'group' && (
        <div>
          <div className="row row--spread section-head">
            <h4 className="section-label">Grupper</h4>
            <button className="btn btn--sm btn--outline" onClick={() => setShowCreateGroup(!showCreateGroup)}>
              {showCreateGroup ? 'Avbryt' : '+ Ny grupp'}
            </button>
          </div>

          {showCreateGroup && (
            <div className="create-group">
              <input
                placeholder="Gruppnamn"
                value={groupName}
                onChange={e => setGroupName(e.target.value)}
                maxLength={100}
              />
              <p className="section-label">Välj medlemmar</p>
              {friends.length === 0 && <p className="empty">Du behöver minst en vän för att skapa en grupp.</p>}
              {friends.map(friend => (
                <label key={friend.id} className="member-option">
                  <input
                    type="checkbox"
                    checked={selectedMembers.includes(friend.id)}
                    onChange={() => toggleMember(friend.id)}
                  />
                  <Avatar src={friend.profile_picture} name={friend.username} size="sm" />
                  <span>{friend.username}</span>
                </label>
              ))}
              <button className="btn btn--primary btn--block" onClick={createGroup}>Skapa grupp</button>
            </div>
          )}

          {groups.length === 0 && !showCreateGroup && <p className="empty">Inga gruppchatter.</p>}

          {groups.map(group => (
            <button
              key={group.id}
              className="convo"
              onClick={() => openConversation({ type: 'group', id: group.id, name: group.name })}
            >
              <Avatar name={group.name} />
              <span className="convo__body">
                <span className="convo__top">
                  <span className="convo__name">{group.name}</span>
                  <span className="convo__time">{formatListTime(group.last_message_at)}</span>
                </span>
                <span className="convo__bottom">
                  <span className="convo__preview">
                    {group.last_message
                      ? `${group.last_message_sender_id === user.id ? 'Du' : group.last_message_sender_name}: ${group.last_message}`
                      : `${group.member_count} medlemmar`}
                  </span>
                </span>
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
